import { useState } from 'react'
import { hrApi } from '@/services/api/hr'
import type { CostBearerRow, DeductionKind, HrFinanceCase, HrSettleOverride, PayoutKind } from '@/types/hr'
import { extractErrorMessage } from '@/utils/errorHandler'
import { FloatInput } from '@/components/ui/FloatInput'

function fmt(n: number): string {
  return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function SettleOverrideDialog({ caseData, bearers, lessonIds, onClose, onSettled }: {
  caseData: HrFinanceCase
  bearers: CostBearerRow[]
  lessonIds?: string[]
  onClose: () => void
  onSettled: (msg: string) => void
}) {
  const [groups, setGroups] = useState(() => caseData.subject_groups.map(g => ({
    subject_id: g.subject_id,
    subject_name: g.subject_name,
    payout: g.default_payout as PayoutKind,
    requester_deduction: (caseData.requester_deduction ?? 'ipb') as DeductionKind,
    cost_bearer_id: '',
    amount: 1 as number | null,
    note: '',
  })))
  const [force, setForce] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const blocked = caseData.warnings.some(w => w.status === 'block')

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      const overrides: HrSettleOverride[] = groups.map(g => ({
        subject_id: g.subject_id,
        payout: g.payout,
        requester_deduction: caseData.payer === 'private' ? g.requester_deduction : undefined,
        cost_bearer_id: g.cost_bearer_id || undefined,
        amount: g.amount == null || g.amount <= 0 ? undefined : g.amount,
        note: g.note.trim() === '' ? undefined : g.note,
        ...(lessonIds?.length === 1 ? { lesson_id: lessonIds[0] } : {}),
      }))
      const r = await hrApi.settleCase(caseData.substitution_request_id, overrides, force, lessonIds)
      onSettled(`Fall abgerechnet: ${r.settled_lessons} Lektion(en) verbucht.`)
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Abrechnung fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5">
        <h2 className="text-lg font-bold mb-1">Fall bearbeiten & abschliessen</h2>
        <p className="text-xs text-gray-500 mb-4">
          {caseData.subject_groups.map(g => g.subject_name).join(', ')} · {caseData.open_lessons} Lektion(en) ·
          Zahler: {caseData.payer === 'private' ? 'Privat' : 'Schule'}
        </p>

        {error && <div className="mb-3 p-2.5 bg-red-100 text-red-700 rounded text-sm">{error}</div>}
        {caseData.warnings.map((w, i) => (
          <div key={i} className="mb-3 p-2.5 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded">
            Negativ-Saldo: {w.teacher} — Saldo {fmt(w.saldo)} → {fmt(w.prospective)} Lekt.
            {w.status === 'block' && (
              <label className="ml-2 inline-flex items-center gap-1.5 font-medium align-middle">
                <button
                  type="button"
                  onClick={() => setForce(f => !f)}
                  role="switch" aria-checked={force} aria-label="trotzdem verbuchen (Force)"
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${force ? 'bg-amber-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${force ? 'translate-x-[18px]' : 'translate-x-1'}`} />
                </button>
                trotzdem verbuchen (Force)
              </label>
            )}
          </div>
        ))}

        <div className="space-y-4">
          {groups.map((g, idx) => (
            <div key={g.subject_id ?? idx} className="bg-white border-l-4 border-l-gray-200 rounded-xl shadow-sm p-3">
              <div className="font-medium text-sm mb-2">{g.subject_name}</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <label className="text-xs text-gray-600">
                  Gutschrift (Vertreter)
                  <select value={g.payout}
                    onChange={e => setGroups(gs => gs.map((x, i) => i === idx ? { ...x, payout: e.target.value as PayoutKind } : x))}
                    className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white">
                    <option value="lesson_account">Lektionenkonto (IPB)</option>
                    <option value="paid_out">Auszahlung</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600">
                  Abbuchung (Anfragender{caseData.payer !== 'private' ? ' — entfällt bei Schul-Zahler' : ''})
                  <select value={g.requester_deduction} disabled={caseData.payer !== 'private'}
                    onChange={e => setGroups(gs => gs.map((x, i) => i === idx ? { ...x, requester_deduction: e.target.value as DeductionKind } : x))}
                    className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white disabled:bg-gray-100">
                    <option value="ipb">IPB</option>
                    <option value="salary">Lohn (extern)</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600">
                  Lektionen pro Lektion (Dezimal)
                  <FloatInput value={g.amount} allowEmpty
                    onChange={v => setGroups(gs => gs.map((x, i) => i === idx ? { ...x, amount: v } : x))}
                    className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5" />
                </label>
                <label className="text-xs text-gray-600">
                  Kostenträger
                  <select value={g.cost_bearer_id}
                    onChange={e => setGroups(gs => gs.map((x, i) => i === idx ? { ...x, cost_bearer_id: e.target.value } : x))}
                    className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white">
                    <option value="">Default ({caseData.payer === 'private' ? 'Privat' : 'IPB'})</option>
                    {bearers.filter(b => b.is_active).map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-gray-600 col-span-2 md:col-span-4">
                  Notiz (optional)
                  <input type="text" value={g.note}
                    onChange={e => setGroups(gs => gs.map((x, i) => i === idx ? { ...x, note: e.target.value } : x))}
                    className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2 py-1.5" />
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Abbrechen</button>
          <button type="button" disabled={busy || (blocked && !force)} onClick={() => void submit()}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
            {busy ? 'Buche...' : 'Abschliessen & verbuchen'}
          </button>
        </div>
      </div>
    </div>
  )
}
